import { type FC, useMemo, useState } from "react";
import { FormattedMessage } from "react-intl";

import { Card } from "common/core/card";
import { Heading, Paragraph, Substyle } from "common/core/typography";
import { Column, Row } from "common/core/responsive";
import { format } from "common/core/format/date";
import Icon from "common/core/icon";
import {
  getGenericDocumentTitleFromType,
  getSpecificDocumentTitleFromClaimedType,
  parsePhotosForDocumentViewer,
  PhotoType,
} from "common/identity/document_viewer";
import DocumentScrutinizer from "common/identity/document_viewer/scrutinizer";
import { Spacer } from "common/details/identity/v2/components/spacer";

import { ImageSelector } from "./actions";
import { parsePhotoRecordToArray, type PhotoInfo } from "./util";
import type { DocumentViewerCustomerSigner as CustomerSigner } from "./index.fragment.graphql";
import Styles from "./index.module.scss";

type Props = {
  customerSigner: CustomerSigner;
};

const DocumentDescription: FC<{ image: PhotoInfo }> = ({ image }) => {
  const { documentClaimedType, side, type } = image;
  const title = useMemo(() => {
    const specificDocumentTitle = getSpecificDocumentTitleFromClaimedType(documentClaimedType);
    return specificDocumentTitle || getGenericDocumentTitleFromType(image.type);
  }, [image]);

  return (
    <Paragraph className={Styles.description}>
      <Substyle size="small" textColor="subtle">
        <Icon name="camera" size="large" />
        {type === PhotoType.Selfie ? (
          <FormattedMessage
            id="a0b1f2c4-3d5e-4f8b-9c7d-6e1a2f3b4c5d"
            defaultMessage="Selfie"
            tagName="span"
          />
        ) : (
          <FormattedMessage
            id="2fd04da7-3e3a-405b-98fd-2dfe3c842cee"
            defaultMessage="{title}, {side, select, front {Front} other {Back}}"
            values={{
              title,
              side,
            }}
            tagName="span"
          />
        )}
        <Spacer subtle />
        <FormattedMessage
          id="0f9fe93d-8614-46a9-9bf3-dbb2e17c306e"
          defaultMessage="Attempt {attempt}"
          values={{ attempt: image.attempt }}
          tagName="span"
        />
        <Spacer subtle />
        <span>{format({ value: image.createdAt, formatStyle: "EEE MMM'.' d yyyy, h:mmaa" })}</span>
      </Substyle>
    </Paragraph>
  );
};

export function DocumentViewer({ customerSigner }: Props) {
  const [imageIndex, setImageIndex] = useState(0);
  const images = useMemo(
    () =>
      customerSigner.signerIdentities?.flatMap((identity, index, arr) =>
        parsePhotoRecordToArray(
          parsePhotosForDocumentViewer(identity),
          arr.length - index,
          identity.createdAt,
        ),
      ) ?? [],
    [customerSigner.signerIdentities],
  );

  const image = useMemo(() => images[imageIndex], [images, imageIndex]);

  if (!images.length) {
    return null;
  }

  return (
    <Row>
      <Column>
        <Card fullWidth noMargin>
          <Heading level="h4" textStyle="allCapsLabelSmall">
            <FormattedMessage id="bf5b8fe4-fc30-4aac-b22d-1b741f09730e" defaultMessage="Photos" />
          </Heading>
          <DocumentScrutinizer
            className={Styles.imageContainer}
            controlButtonClassName={Styles.controlButton}
            key={image.src}
            src={{
              type: image.src.endsWith(".pdf") ? "pdf" : "image",
              url: image.src,
            }}
          />
          <DocumentDescription image={image} />
          <ImageSelector imageIndex={imageIndex} setImageIndex={setImageIndex} images={images} />
        </Card>
      </Column>
    </Row>
  );
}
